import { ROLES } from '@/config/roles'
import { getUserByUid } from '@/services/userService'
import { crossYouthBoundary } from '@/config/youthEnvironment'

/**
 * Redirect user based on their role and account status
 * @param {string} role - User role
 * @param {Object} currentUser - Current user object from auth
 * @param {Function} navigate - Navigation function from react-router
 * @param {boolean} emailVerified - Optional email verification status
 * @returns {Promise<void>}
 */
export const redirectBasedOnRole = async (
  role,
  currentUser,
  navigate,
  emailVerified = null
) => {
  if (!role || !currentUser || !navigate) {
    console.error('Redirect based on role: Missing required parameters')
    navigate('/')
    return
  }

  switch (role) {
    case ROLES.UNDEFINED:
      navigate('/auth/role-select')
      break
    case ROLES.SCHOOL_ADMIN: {
      // If emailVerified is explicitly passed as true, trust it and proceed
      if (emailVerified === true) {
        const schoolAdminUser = await getUserByUid(currentUser.uid)

        // 1. Check if firstName or lastName is missing → account-setup
        if (!schoolAdminUser?.firstName || !schoolAdminUser?.lastName) {
          navigate('/onboarding/educator/account-setup')
        }
        // 2. Has name but no subscription → plan-options
        else if (!schoolAdminUser?.hasSubscription) {
          navigate('/school-plan-options')
        }
        // 3. Has name and subscription but not onboarded → school-onboarding
        else if (!schoolAdminUser?.isSchoolOnboarded) {
          navigate('/educator-access/school-onboarding')
        }
        // 4. Has name, subscription, and onboarded → dashboard
        else {
          navigate('/dashboard/educator')
        }
        break
      }

      // If emailVerified is not explicitly true, check Firestore
      const schoolAdminUser = await getUserByUid(currentUser.uid)
      const isEmailVerified =
        emailVerified !== null
          ? emailVerified
          : schoolAdminUser?.emailVerified !== false

      // If email is not verified, don't redirect - user should verify email first
      // This prevents redirecting to payment page before email verification
      if (
        isEmailVerified === false ||
        schoolAdminUser?.emailVerified === false
      ) {
        // Don't redirect - let the verification flow handle navigation
        // User should be on verification page or will be redirected there
        return
      }

      // Use fresh user data for onboarding and subscription checks
      // 1. Check if firstName or lastName is missing → account-setup
      if (!schoolAdminUser?.firstName || !schoolAdminUser?.lastName) {
        navigate('/onboarding/educator/account-setup')
      }
      // 2. Has name but no subscription → plan-options
      else if (!schoolAdminUser?.hasSubscription) {
        navigate('/school-plan-options')
      }
      // 3. Has name and subscription but not onboarded → school-onboarding
      else if (!schoolAdminUser?.isSchoolOnboarded) {
        navigate('/educator-access/school-onboarding')
      }
      // 4. Has name, subscription, and onboarded → dashboard
      else {
        navigate('/dashboard/educator')
      }
      break
    }
    case ROLES.ADMIN:
      navigate('/dashboard/admin')
      break
    case ROLES.TEACHER:
      navigate('/dashboard/teacher/classroom')
      break
    case ROLES.STUDENT:
      crossYouthBoundary('/dashboard/student/missions')
      break
    case ROLES.CHILD:
      crossYouthBoundary('/dashboard/child/missions')
      break
    case ROLES.PARENT: {
      // Always fetch fresh user data from Firestore to get accurate status
      const user = await getUserByUid(currentUser.uid)
      
      // Check email verification status - prioritize Firestore data over context
      // If emailVerified is explicitly passed as true, trust it and proceed
      // Otherwise, check Firestore user data
      let isEmailVerified = false
      if (emailVerified === true) {
        // Explicitly passed as true, trust it
        isEmailVerified = true
      } else {
        // Check Firestore - if emailVerified is true in Firestore, use that
        // Only consider it false if Firestore explicitly says false
        isEmailVerified = user?.emailVerified !== false
      }

      // Only return early if email is explicitly not verified in Firestore
      // Don't block redirect if context says false but Firestore says true/undefined
      if (isEmailVerified === false && user?.emailVerified === false) {
        // Email is not verified - don't redirect, let verification flow handle it
        return
      }

      // Proceed with redirect based on user status
      if (user?.isOnboarded && user?.hasSubscription) {
        navigate('/dashboard/parent/profiles')
      } else if (!user?.hasSubscription) {
        navigate('/onboarding/parent/child-account-type')
      } else if (!user?.children && user?.hasSubscription) {
        navigate('/onboarding/parent/children-account-details')
      } else if (!user?.isOnboarded && user?.hasSubscription) {
        navigate('/dashboard/parent/profiles')
      } else {
        // Default fallback - redirect to profiles page
        navigate('/dashboard/parent/profiles')
      }
      break
    }
    default:
      navigate('/')
  }
}
